/**
 * Date utility functions for calendar-based slot booking system
 */

/**
 * Format date to YYYY-MM-DD for API
 * @param {Date|string} date - Date to format
 * @returns {string|null} Formatted date string or null
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

/**
 * Check if date is beyond max future days
 * @param {Date|string} date - Date to check
 * @param {number} maxDays - Maximum number of days in the future (default: 30)
 * @returns {boolean} True if date is beyond max days
 */
export const isBeyondMaxDays = (date, maxDays = 30) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxDays);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate > maxDate;
};

/**
 * Get date range for calendar (today to today + 30 days)
 * @returns {{minDate: Date, maxDate: Date}} Valid date range
 */
export const getValidDateRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 30);
  return { minDate: today, maxDate };
};

/**
 * Format date for display (e.g., "November 9, 2025")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate.getTime() === today.getTime();
};

/**
 * Check if date is valid for selection (not past, not beyond max days)
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is valid
 */
export const isValidDate = (date) => {
  return !isPastDate(date) && !isBeyondMaxDays(date);
};
